<template>
    <b-modal
        id="show-group-modal"
        v-model="display"
        :title="`${$t('group_pair.request')} ${subject}`"
        :ok-title="canEdit() && tabIndex === 0 ? $t('common.save') : $t('common.close')"
        cancel-variant="outline-primary"
        :cancel-title="$t('common.cancel')"
        size="lg"
        dialog-class="assignment-modal"
        modal-class="apprentice-default-modal group-pair-application-overview-modal"
        @ok="saveGroup">
        <b-tabs
            class="apprentice-tab"
            v-model="tabIndex"
            @input="updateTabIndex">
            <b-tab :title="$t('group_pair.content')">
                <div class="group-pair-application-overview">
                    <b-alert
                        v-if="groupPairHasStudents"
                        show
                        variant="danger">
                        {{
                            $t(
                                'group_pair.watch_out_if_there_are_no_more_students_in_this_group_the_group_will_be_deleted',
                            )
                        }}
                    </b-alert>

                    <div class="motivation">
                        <div class="heading">
                            {{ $t('group_pair.motivation') }}

                            <b-button
                                class="btn-download btn-group-pair-attachment"
                                v-b-tooltip.hover
                                v-if="group_pair_group_file && group_pair_group_file.length"
                                variant="primary"
                                :title="$t('common.download')"
                                :href="group_pair_group_file[0].file.public_path"
                                :download="group_pair_group_file[0].file.full_name">
                                {{ $t('group_pair.download_appendix') }}
                            </b-button>
                        </div>

                        <div class="group-pair-motivation-description">
                            {{ explanation }}
                        </div>
                    </div>

                    <div
                        class="group-pair-courses"
                        v-if="courses.length > 0">
                        <div class="heading">{{ $t('group_pair.profile_courses') }}</div>
                        <b-badge
                            class="group-pair-course"
                            v-for="course of courses"
                            variant="primary"
                            :key="course.id">
                            {{ course.title }}
                        </b-badge>
                    </div>

                    <ul class="group-pair-students">
                        <li class="heading">{{ $t('group_pair.students') }}</li>
                        <li
                            class="group-pair-student"
                            v-for="student of group_pair_students"
                            :key="student.id">
                            <span class="name">{{ student.user.full_name }}</span>

                            <b-dropdown
                                class="group-pair-student-courses"
                                split
                                right
                                :ref="`student-course-${student.id}`"
                                split-variant="outline-primary"
                                variant="primary"
                                :text="$t('group_pair.profile_courses')"
                                @click="showStudentCourses(student.id)">
                                <b-dropdown-text
                                    v-if="!student.courses || student.courses.length === 0"
                                    text-class="not-found">
                                    {{ $t('group_pair.no_profile_courses_chosen') }}
                                </b-dropdown-text>

                                <b-dropdown-text
                                    v-for="course in student.courses"
                                    v-else
                                    :key="course.id">
                                    {{ course.name }}
                                </b-dropdown-text>
                            </b-dropdown>

                            <b-button
                                class="btn-remove btn-remove-student"
                                v-if="!removedGroupPairStudentIds.includes(student.id) && canEdit()"
                                v-b-tooltip.hover
                                variant="danger-icon-only"
                                :title="$t('common.delete')"
                                @click="deleteStudent(student.id)">
                            </b-button>

                            <b-button
                                class="btn-undo-remove btn-restore-student"
                                v-if="removedGroupPairStudentIds.includes(student.id)"
                                v-b-tooltip.hover
                                variant="success-icon-only"
                                :title="$t('group_pair.set_back')"
                                @click="restoreStudent(student.id)">
                                <i class="fas fa-trash-undo-alt"></i>
                            </b-button>
                        </li>
                    </ul>

                    <ul class="group-pair-teachers">
                        <li class="heading">{{ $t('group_pair.teachers') }}</li>
                        <li
                            class="group-pair-teacher not-found"
                            v-if="teachers.length === 0">
                            {{ $t('group_pair.no_teachers_chosen') }}
                        </li>

                        <li
                            class="group-pair-teacher"
                            v-for="teacher in teachers"
                            v-else
                            :key="teacher.id">
                            <span class="name">{{ teacher.name }}</span>
                            <b-button
                                class="btn-remove btn-remove-teacher"
                                v-b-tooltip.hover
                                v-if="
                                    status !== 2 &&
                                    groupPairOptionsIn.check_option !== 3 &&
                                    !deletedTeachers.includes(teacher.id) &&
                                    canEdit()
                                "
                                variant="danger-icon-only"
                                :title="$t('group_pair.delete_teacher')"
                                @click="deleteTeacher(teacher.id)">
                            </b-button>

                            <b-button
                                class="btn-undo-remove btn-restore-teacher"
                                v-if="
                                    status !== 2 &&
                                    groupPairOptionsIn.check_option !== 3 &&
                                    deletedTeachers.includes(teacher.id)
                                "
                                variant="success-icon-only"
                                :title="$t('group_pair.restore_teacher')"
                                @click="restoreTeacher(teacher.id)">
                                <i class="fas fa-trash-undo-alt"></i>
                            </b-button>
                        </li>
                    </ul>

                    <div class="group-pair-approve">
                        <div class="heading">{{ this.$t('group_pair.approval') }}</div>
                        <div
                            v-if="groupPairOptionsIn.check_option === 1 || groupPairOptionsIn.check_option === 2"
                            :class="['group-pair-approve-teacher', !canEdit() && 'disable-click']">
                            <b-form-group :disabled="(!isTeacher() && !$authUser.isAdmin()) || !canEdit()">
                                <p
                                    class="sub-heading"
                                    v-if="groupPairOptionsIn.check_option === 2">
                                    Docent:
                                </p>
                                <b-form-radio-group
                                    class="apprentice-radiobutton"
                                    v-model="check_teacher"
                                    :options="options"
                                    name="radios-teacher"
                                    stacked>
                                </b-form-radio-group>
                            </b-form-group>
                        </div>

                        <div
                            class="group-pair-approve-coordinator"
                            v-if="groupPairOptionsIn.check_option === 2 || groupPairOptionsIn.check_option === 3">
                            <b-form-group :disabled="!coordinatorIn && !$authUser.isAdmin()">
                                <p
                                    class="sub-heading"
                                    v-if="groupPairOptionsIn.check_option === 2">
                                    {{ $t('group_pair.administrator') }}
                                </p>
                                <b-form-radio-group
                                    class="apprentice-radiobutton"
                                    v-model="check_coordinator"
                                    :options="options"
                                    name="radios-coordinator"
                                    stacked>
                                </b-form-radio-group>
                            </b-form-group>
                        </div>
                    </div>

                    <div class="group-pair-feedback">
                        <div class="heading">Feedback</div>
                        <b-form-group>
                            <b-form-textarea
                                class="apprentice-textarea"
                                id="textarea"
                                v-model="feedback"
                                :disabled="
                                    (groupPairOptionsIn.check_option === 3 &&
                                        coordinatorIn === false &&
                                        !$authUser.isAdmin()) ||
                                    !canEdit()
                                "
                                rows="4"
                                max-rows="8"
                                :placeholder="$t('group_pair.place_your_feedback_here')">
                            </b-form-textarea>
                        </b-form-group>
                    </div>
                </div>
                <b-alert
                    v-if="removedGroupPairStudentIds.length >= group_pair_students.length"
                    show
                    variant="danger">
                    {{
                        $t('group_pair.watch_out_if_there_are_no_more_students_in_this_group_the_group_will_be_deleted')
                    }}
                </b-alert>
            </b-tab>

            <b-tab title="Log">
                <design-2021-group-pair-log-component :activities-in="activities">
                </design-2021-group-pair-log-component>
            </b-tab>
        </b-tabs>
    </b-modal>
</template>

<script>
import $ from 'jquery';

export default {
    name: 'design-2021-group-pair-index-show-group-modal',
    props: ['coordinatorIn', 'groupPairOptionsIn'],
    data() {
        return {
            options: [
                { text: this.$t('group_pair.approved'), value: 2 },
                { text: this.$t('group_pair.again_reject'), value: 1 },
            ],
            groupPairGroup: null,
            feedback: '',
            check_coordinator: null,
            check_teacher: null,

            deletedTeachers: [],
            teachers: [],
            subject: '',
            explanation: '',
            courses: '',
            accepted: 0,
            group_pair_students: [],
            group_pair_group_teachers: [],
            removedGroupPairStudentIds: [],
            group_pair_group_file: [],
            status: 0,
            activities: [],
            display: false,
            hiddenFields: ['courses', 'students', 'teachers', 'subject', 'explanation', 'feedback'],
            tabIndex: 0,
        };
    },

    methods: {
        showStudentCourses(studentId) {
            const refName = `student-course-${studentId}`;
            this.$refs[refName][0].$el.children[1].click();
        },

        deleteStudent(group_pair_student_id) {
            this.removedGroupPairStudentIds.push(group_pair_student_id);
        },

        restoreStudent(id) {
            this.removedGroupPairStudentIds = this.removedGroupPairStudentIds.filter(
                (group_pair_student_id) => group_pair_student_id !== id,
            );
        },

        show(groupPairGroup, tab) {
            this.groupPairGroup = groupPairGroup;
            this.subject = groupPairGroup.subject;
            this.explanation = groupPairGroup.explanation;
            this.feedback = groupPairGroup.feedback;
            this.check_coordinator = groupPairGroup.check_coordinator;
            this.check_teacher = groupPairGroup.check_teacher;
            this.deletedTeachers = [];
            this.group_pair_students = groupPairGroup.group_pair_students;
            this.group_pair_group_teachers = groupPairGroup.group_pair_group_teachers;
            this.status = groupPairGroup.status;
            this.group_pair_group_file = groupPairGroup.group_pair_group_file;
            this.teachers = groupPairGroup.group_pair_group_teachers.map((teacher) => {
                return {
                    id: teacher.id,
                    name: teacher.user.full_name,
                };
            });

            this.accepted = groupPairGroup.accepted;
            this.courses = groupPairGroup.group_pair_group_courses.map((course) => {
                return {
                    title: course.course.name,
                    id: course.course.id,
                };
            });

            this.activities = groupPairGroup.activities;
            this.tabIndex = tab === 'Log' ? 1 : 0;
            this.display = true;
        },

        isTeacher() {
            for (const teacher of this.group_pair_group_teachers) {
                if (teacher.user_id === this.$authUser.id) {
                    return true;
                }
            }
            return false;
        },

        deleteTeacher(id) {
            this.deletedTeachers.push(id);
        },

        restoreTeacher(id) {
            this.deletedTeachers = this.deletedTeachers.filter((teacher_id) => teacher_id !== id);
        },

        getStatus(option) {
            if (this.accepted === 1) {
                return 2;
            }

            switch (option) {
                case 1:
                    return this.check_teacher === 1 || this.check_teacher === 2 ? this.check_teacher : 3;
                case 2:
                    if (this.check_teacher === 2 && this.check_coordinator === 2) {
                        return 2; // approved
                    } else if (!this.check_teacher || !this.check_coordinator) {
                        return 3; // empty
                    } else {
                        return 1; // redo
                    }
                case 3:
                    return this.check_coordinator === 1 || this.check_coordinator === 2 ? this.check_coordinator : 3;
            }
            return undefined;
        },

        saveGroup(bvmodalevent) {
            if (!this.canEdit() || this.tabIndex === 1) {
                this.$bvModal.hide();
            } else {
                let groupPairGroup = {
                    remove_teacher_ids: this.deletedTeachers,
                    status_teacher: this.check_teacher,
                    status_coordinator: this.check_coordinator,
                    feedback: this.feedback,
                    id: this.groupPairGroup.id,
                    removed_group_pair_student_ids: this.removedGroupPairStudentIds,
                };

                if (this.coordinatorIn && !this.check_coordinator && this.groupPairOptionsIn.check_option >= 2) {
                    bvmodalevent.preventDefault();
                    this.alert.warning(
                        this.$t('group_pair.approval_as_teacher'),
                        this.$t('group_pair.to_save_the_application_approval_or_rejection_must_be_submitted'),
                    );
                    return;
                } else if (this.isTeacher(this.groupPairGroup) && !this.check_teacher) {
                    bvmodalevent.preventDefault();
                    this.alert.warning(
                        this.$t('group_pair.approval_as_teacher'),
                        this.$t('group_pair.to_save_the_application_approval_or_rejection_must_be_submitted'),
                    );
                    return;
                }

                if (this.getStatus(this.groupPairOptionsIn.check_option) === 2 && this.currentStatus !== 2) {
                    this.alert
                        .confirm(
                            this.$t('group_pair.reopen_groups'),
                            this.$t(
                                'group_pair.a_paper_has_already_been_created_for_this_group_if_you_reopen_this_group_and_close_it_again_later_another_paper_will_be_created_for_this_group',
                            ),
                            this.$t('group_pair.reopen'),
                        )
                        .then((confirm) => {
                            if (confirm) {
                                this.$emit('saveCall', groupPairGroup);
                            }
                        });
                } else {
                    if (this.currentStatus === 2 && this.getStatus(this.groupPairOptionsIn.check_option) !== 2) {
                        let taken_teachers = [];
                        // we could do this in back-end
                        for (const teacher of this.groupPairGroup.group_pair_group_teachers) {
                            if (
                                teacher.user.groupPairGroupTeacherIsFull(
                                    this.groupPairOptionsIn.check_option,
                                    this.groupPairGroup.group_id,
                                )
                            ) {
                                taken_teachers.push(teacher.user.full_name);
                            }
                        }

                        if (taken_teachers.length) {
                            this.alert
                                .confirm(
                                    this.$t('group_pair.teacher_max_reached'),
                                    this.$t('group_pair.the_following_teachers_are_at_their_maximum_number_of_groups', {
                                        teachers: taken_teachers.join(', '),
                                    }),
                                    this.$t('group_pair.approve'),
                                )
                                .then((confirm) => {
                                    if (confirm) {
                                        this.$emit('saveCall', groupPairGroup);
                                    }
                                });
                        } else {
                            this.$emit('saveCall', groupPairGroup);
                        }
                    } else {
                        this.$emit('saveCall', groupPairGroup);
                    }
                }
            }
        },

        canEdit() {
            if (this.groupPairOptionsIn.deleted_at) {
                return false;
            }

            return (
                (this.isTeacher(this.groupPairGroup) && this.groupPairGroup.check_teacher === null) ||
                this.coordinatorIn ||
                this.$authUser.isAdmin()
            );
        },

        showLogModal(log) {
            this.$refs['show-log-modal'].show(log);
        },

        updateTabIndex(index) {
            this.tabIndex = index;
        },
    },

    computed: {
        currentStatus() {
            switch (this.groupPairOptionsIn.check_option) {
                case 1:
                    return this.check_teacher === 1 || this.check_teacher === 2 ? this.check_teacher : 3;
                case 2:
                    if (this.check_teacher === 2 && this.check_coordinator === 2) {
                        return 2; // approved
                    } else if (!this.check_teacher || !this.check_coordinator) {
                        return 3; // empty
                    } else {
                        return 1; // redo
                    }
                case 3:
                    return this.check_coordinator === 1 || this.check_coordinator === 2 ? this.check_coordinator : 3;
            }
            return undefined;
        },

        groupPairHasStudents() {
            if (this.removedGroupPairStudentIds.length >= this.group_pair_students.length) {
                $('.group-pair-application-overview-modal').animate({ scrollTop: 0 }, 500);
                return true;
            }

            return false;
        },
    },
};
</script>

<style scoped lang="scss">
.heading {
    font-weight: $font-weight-medium;
    font-size: 1.125rem;
}

ul {
    padding: 0;

    li {
        list-style: none;
    }
}

.group-pair-application-overview {
    > div:not(:last-child),
    > ul:not(:last-child) {
        margin-bottom: $grid-gutter-width;
    }
}

.motivation {
    .heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    .btn-group-pair-attachment {
        width: auto;
    }

    .group-pair-motivation-description {
        border-radius: $border-radius-content;
        background-color: $border-color;
        color: $text-color;
        min-height: 100px;
        padding: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.group-pair-courses {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: map-get($grid-gutter-spacing, 'ten');
    row-gap: map-get($grid-gutter-spacing, 'fifteen');

    .heading {
        flex: 1 0 100%;
    }

    .badge {
        font-size: 0.75rem;
        font-weight: $font-weight-medium;
        padding: map-get($grid-gutter-spacing, 'four') map-get($grid-gutter-spacing, 'eight');
    }
}

.group-pair-student,
.group-pair-teacher {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');
    padding: map-get($grid-gutter-spacing, 'eight');
    border-radius: 8px;

    .btn-remove,
    .btn-undo-remove {
        width: 23px;
        font-size: 1.125rem;
    }

    &:not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    &:nth-child(odd) {
        background-color: $secondary-background-color;
    }
}

.group-pair-student {
    .name {
        flex: 1 0 100%;
    }

    .dropdown ::v-deep {
        .btn {
            line-height: 1;
            padding: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'ten');
            font-size: $default-font-size;
        }

        li {
            width: 250px;
        }

        .b-dropdown-text {
            color: $primary;
            font-weight: $font-weight-medium;
            font-size: $default-font-size;

            &.not-found {
                color: $text-muted-color;
                font-weight: $font-weight-normal;
            }
        }
    }
}

.group-pair-teacher {
    .name {
        flex: 0 1 calc(100% - 45px);
    }

    .btn {
        flex: 1 0 auto;
    }

    &.not-found {
        padding: map-get($grid-gutter-spacing, 'eight') 0;
        color: $text-muted-color;
        align-items: flex-start;
    }
}

.group-pair-approve {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    column-gap: map-get($grid-gutter-spacing, 'sixty');

    .heading {
        flex: 1 0 100%;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .group-pair-student {
        .name {
            flex: 0 1 calc(100% - 170px);
        }
    }
}
</style>
